import { apiClient } from './api';
import type {
  WorkoutSession,
  CreateWorkoutSessionInput,
  ApiCollection,
  SessionLogData,
  SessionEditData,
  SessionEditPayload,
  FocusedProgressSummaryResponse,
  WorkoutTrainerReport,
} from './types';

export class WorkoutSessionService {
  private static readonly ENDPOINT = '/api/workout_sessions';

  static async getAll(): Promise<WorkoutSession[]> {
    const response = await apiClient.get<ApiCollection<WorkoutSession>>(this.ENDPOINT);
    return response.data['member'];
  }

  static async getById(id: number): Promise<WorkoutSession> {
    const response = await apiClient.get<WorkoutSession>(`${this.ENDPOINT}/${id}`);
    return response.data;
  }

  static async create(session: CreateWorkoutSessionInput): Promise<WorkoutSession> {
    const response = await apiClient.post<WorkoutSession>(this.ENDPOINT, session);
    return response.data;
  }

  static async update(id: number, session: Partial<CreateWorkoutSessionInput>): Promise<WorkoutSession> {
    const response = await apiClient.patch<WorkoutSession>(`${this.ENDPOINT}/${id}`, session);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.ENDPOINT}/${id}`);
  }

  // Utility methods
  static async getByTrainingProgram(trainingProgramId: number): Promise<WorkoutSession[]> {
    const sessions = await this.getAll();
    return sessions.filter(session => 
      session.trainingProgram === `/api/training_programs/${trainingProgramId}`
    );
  }

  static async getByStatus(status: WorkoutSession['status']): Promise<WorkoutSession[]> {
    const sessions = await this.getAll();
    return sessions.filter(session => session.status === status);
  }

  static async getByDateRange(startDate: string, endDate: string): Promise<WorkoutSession[]> {
    const params = new URLSearchParams({
      'workoutDate[after]': startDate,
      'workoutDate[before]': endDate,
      'order[workoutDate]': 'desc',
    });
    const response = await apiClient.get<ApiCollection<WorkoutSession>>(`${this.ENDPOINT}?${params.toString()}`);
    return response.data['member'];
  }

  /**
   * Acquire a completion lock on a workout session.
   * Prevents concurrent completion attempts (double-click protection).
   * Returns true if lock acquired, false if session is already locked or completed.
   */
  static async acquireCompletionLock(id: number): Promise<boolean> {
    try {
      await apiClient.post(`/api/workout-sessions/${id}/lock-completion`, {});
      return true;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        return false;
      }
      throw err;
    }
  }

  static async markAsCompleted(id: number): Promise<WorkoutSession> {
    return this.update(id, {
      status: 'completed',
      completedAt: new Date().toISOString(),
    });
  }

  static async markAsInProgress(id: number): Promise<WorkoutSession> {
    return this.update(id, { status: 'in_progress' });
  }

  static async markAsSkipped(id: number): Promise<WorkoutSession> {
    return this.update(id, { status: 'skipped' });
  }

  static async getLogData(sessionId: number): Promise<SessionLogData> {
    const response = await apiClient.get<SessionLogData>(`/api/workout-sessions/${sessionId}/log-data`);
    return response.data;
  }

  static async getFocusedProgressSummary(): Promise<FocusedProgressSummaryResponse> {
    const response = await apiClient.get<FocusedProgressSummaryResponse>('/api/workout-progress/focused-summary');
    return response.data;
  }

  static async getEditData(sessionId: number): Promise<SessionEditData> {
    const response = await apiClient.get<SessionEditData>(`/api/workout-sessions/${sessionId}/edit-data`);
    return response.data;
  }

  static async updateSets(sessionId: number, payload: SessionEditPayload): Promise<void> {
    await apiClient.put(`/api/workout-sessions/${sessionId}/sets`, payload);
  }

  static async updateLogRow(
    sessionId: number,
    currentExerciseId: number,
    payload: { exerciseId: number; perSets: Array<{ reps: number; weight: number }> }
      | { exerciseId: number; sets: number; reps: number; weight: number }
  ): Promise<{ row: { exerciseId: number; exerciseName: string; sets: number; perSets?: Array<{ reps: number; weight: number }> } }> {
    const response = await apiClient.put<{ row: { exerciseId: number; exerciseName: string; sets: number; perSets?: Array<{ reps: number; weight: number }> } }>(
      `/api/logs/strength/${sessionId}/exercise/${currentExerciseId}`,
      payload
    );
    return response.data;
  }

  static async getTrainerReports(sessionIds: number[]): Promise<Record<number, WorkoutTrainerReport>> {
    if (sessionIds.length === 0) {
      return {};
    }

    const uniqueSessionIds = Array.from(new Set(sessionIds.filter((id) => Number.isInteger(id) && id > 0)));
    if (uniqueSessionIds.length === 0) {
      return {};
    }

    const query = encodeURIComponent(uniqueSessionIds.join(','));
    const response = await apiClient.get<{ reports?: WorkoutTrainerReport[] }>(
      `/api/coach-notifications/workout-reports?sessionIds=${query}`
    );

    const reportsBySessionId: Record<number, WorkoutTrainerReport> = {};
    for (const report of response.data.reports ?? []) {
      reportsBySessionId[report.workoutSessionId] = report;
    }

    return reportsBySessionId;
  }

  static async getWeeklyMuscleSets(dateFrom?: string, dateTo?: string, programId?: number): Promise<{ sets: Record<string, number>; dateFrom: string; dateTo: string }> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (programId !== undefined) params.append('programId', programId.toString());
    const qs = params.toString();
    const response = await apiClient.get<{ sets: Record<string, number>; dateFrom: string; dateTo: string }>(
      `/api/workout-progress/weekly-muscle-sets${qs ? '?' + qs : ''}`
    );
    return response.data;
  }
}

export default WorkoutSessionService;
